import {parseNumber} from '@benchling/js-utils';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import FormInput from 'ui-library/bs/FormInput';
import {ElementKeyboardEvent, ElementMouseEvent, InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

export interface IntermediateValueControlledNumericInputProps {
  className?: string;
  focusOnMount?: boolean;
  isDisabled?: boolean;
  min?: number;
  // Whether or not a value exactly equal to `min` is valid. Defaults to true.
  minIsInclusive?: boolean;
  max?: number;
  // Whether or not a value exactly equal to `max` is valid. Defaults to true.
  maxIsInclusive?: boolean;
  onChange: (e: NumericInputChangeEvent) => void;
  onClick?: (e: ElementMouseEvent) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  required?: boolean;
  value?: string | number | null;
  validateInteger?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/*
 *  A wrapper around ControlledNumericInput that enables intermediate values to be displayed properly, for
 *  example when using decimals or scientific notation. (Using ControlledNumericInput makes it difficult
 *  but not impossible to type decimals/scientific notation, since a value like `1.` will be parsed as `1`
 *  and the display value will not change.)
 */
export function IntermediateValueControlledNumericInput(
  props: IntermediateValueControlledNumericInputProps
): JSX.Element {
  // To handle intermediate decimal and scientific notation values, e.g. `.` or `1e`, save the value displayed
  // in the input and pass it through while it matches the props.
  const [displayValue, setDisplayValue] = useState((props.value ?? '').toString());
  const {onChange, value, ...inheritedProps} = props;
  return (
    <ControlledNumericInput
      {...inheritedProps}
      onChange={(e: NumericInputChangeEvent) => {
        props.onChange(e);
        setDisplayValue(e.target.value);
      }}
      // If value and displayValue don't match, fallback to value so we reflect prop changes correctly.
      value={parseNumber(displayValue) !== props.value ? props.value : displayValue}
    />
  );
}

export interface ControlledNumericInputProps extends IntermediateValueControlledNumericInputProps {
  isDisabled: boolean;
  required: boolean;
  validateInteger: boolean;
}

/*
 *  A component that simulates an HTML input with `type: number`. This component accepts `number` values as
 *  props, rather than `string` values, and handles the number -> conversion for display in the `input`.
 *  It also omits the increment/decrement button on `type: number` inputs, and allows null numeric values.
 *
 *  Calls the onChange prop with a modified event, with the numeric value in `e.target.numericValue` and
 *  whether or not the input is valid in `e.target.isValid`.
 */

class ControlledNumericInput extends React.Component<ControlledNumericInputProps> {
  static propTypes = {
    className: PropTypes.string,
    focusOnMount: PropTypes.bool,
    isDisabled: PropTypes.bool.isRequired,
    min: PropTypes.number,
    max: PropTypes.number,
    // Called with the HTML event, but with an additional `e.target.numericValue`, which is the numeric
    // representation of `e.target.value` if it's a valid number, otherwise null. `e.target.value` is left
    // unmodified.
    //
    // Remember to access `e.target.numericValue` in parent components for the number!
    onChange: PropTypes.func.isRequired,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    placeholder: PropTypes.string,
    required: PropTypes.bool.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    validateInteger: PropTypes.bool.isRequired,
  };

  static defaultProps: Partial<ControlledNumericInputProps> = {
    minIsInclusive: true,
    maxIsInclusive: true,
    isDisabled: false,
    required: false,
    validateInteger: false,
    dataTestAttrs: {component: 'ControlledNumericInput', element: 'root'},
  };

  private handleChange = (e: InputChangeEvent): void => {
    const isValid = !this.isInvalid(e.target.value);
    const numericValue = isValid ? parseNumber(e.target.value) : null;
    this.props.onChange({target: {value: e.target.value, isValid, numericValue}});
  };

  private isInvalid = (value: string | number | null | undefined): boolean => {
    if (value == null || value === '') {
      return this.props.required;
    }

    const numericValue = typeof value === 'number' ? value : parseNumber(value);

    if (numericValue == null) {
      return true;
    }
    if (this.props.validateInteger && !Number.isInteger(numericValue)) {
      return true;
    }
    if (
      this.props.min != null &&
      (this.props.minIsInclusive ? numericValue < this.props.min : numericValue <= this.props.min)
    ) {
      return true;
    }
    if (
      this.props.max != null &&
      (this.props.maxIsInclusive ? numericValue > this.props.max : numericValue >= this.props.max)
    ) {
      return true;
    }
    return false;
  };

  render(): JSX.Element {
    const stringValue = (this.props.value ?? '').toString();
    return (
      <FormInput
        className={classNames(this.props.className, {
          'fieldInput-input--error': this.isInvalid(this.props.value),
        })}
        disabled={this.props.isDisabled}
        focusOnMount={this.props.focusOnMount}
        onChange={this.handleChange}
        onClick={this.props.onClick}
        onKeyDown={this.props.onKeyDown}
        placeholder={this.props.placeholder}
        value={stringValue}
        {...getTestProps(this.props.dataTestAttrs)}
        error={this.isInvalid(this.props.value) ? 'Invalid input' : undefined}
      />
    );
  }
}

export default ControlledNumericInput;
